import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { toast } from "sonner";

/**
 * PaymentCompletePage
 * -------------------
 * Stripe's return_url lands here after the embedded checkout completes.
 * We poll GET /api/payments/status/{session_id} — the server will confirm
 * with Stripe, materialise the order from the pending checkout_intent,
 * and return the freshly-created order. If Stripe reports the session as
 * unpaid/canceled, we show an error and NO order is created.
 */
export default function PaymentCompletePage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const navigate = useNavigate();
    const { clear } = useCart();
    const [state, setState] = useState("verifying");   // verifying | paid | failed
    const [order, setOrder] = useState(null);
    const clearedRef = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setState("failed");
            return;
        }
        let mounted = true;
        let attempts = 0;
        const poll = async () => {
            attempts += 1;
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (!mounted) return;
                if (data.status === "paid" && data.order) {
                    if (!clearedRef.current) { clear(); clearedRef.current = true; }
                    setOrder(data.order);
                    setState("paid");
                    toast.success("Payment received — order confirmed.", { duration: 5000 });
                    return;
                }
            } catch {
                // network hiccup — keep polling
            }
            if (attempts >= 15) {
                if (mounted) setState("failed");
                return;
            }
            setTimeout(poll, 2000);
        };
        poll();
        return () => { mounted = false; };
    }, [sessionId, clear]);

    if (state === "verifying") {
        return (
            <div className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
                <div className="max-w-lg mx-auto px-6 text-center">
                    <div className="card p-10 space-y-5">
                        <Loader2 size={40} className="text-brand animate-spin mx-auto" />
                        <div>
                            <h1 className="font-serif text-3xl">Verifying payment…</h1>
                            <p className="text-secondary mt-2 text-sm">Confirming with Stripe. Don't close this tab.</p>
                        </div>
                        <div className="data-label text-[0.6rem] text-muted">Session: {sessionId?.slice(0, 20)}…</div>
                    </div>
                </div>
            </div>
        );
    }

    if (state === "failed") {
        return (
            <div className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
                <div className="max-w-lg mx-auto px-6 text-center">
                    <div className="card p-10 space-y-5">
                        <XCircle size={40} className="text-red-500 mx-auto" />
                        <div>
                            <h1 className="font-serif text-3xl">Payment not completed</h1>
                            <p className="text-secondary mt-2 text-sm">
                                Your card was not charged and no order was created. If you believe this is
                                an error, please contact <a className="text-brand" href="mailto:support@aminoplex.bio">support@aminoplex.bio</a>.
                            </p>
                        </div>
                        <button onClick={() => navigate("/checkout")} className="btn btn-solid w-full" data-testid="retry-payment">
                            Return to Checkout
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Paid — clean confirmation
    return (
        <div className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-2xl mx-auto px-6 space-y-6">
                <div className="card p-10 text-center space-y-4">
                    <div className="inline-flex w-14 h-14 rounded-full bg-[var(--brand-soft)] items-center justify-center">
                        <CheckCircle2 size={30} className="text-brand" />
                    </div>
                    <div>
                        <div className="data-label text-brand mb-1">ORDER CONFIRMED</div>
                        <h1 className="font-serif text-4xl">Thank you, your order is paid.</h1>
                    </div>
                    <div className="flex items-center justify-center gap-3 text-secondary text-sm">
                        <ShieldCheck size={14} className="text-brand" />
                        <span>Confirmation sent to {order?.email}</span>
                    </div>
                </div>

                <div className="card p-8 space-y-4">
                    <div className="flex justify-between items-baseline">
                        <div>
                            <div className="data-label">Order Number</div>
                            <div className="font-mono text-2xl text-brand mt-1" data-testid="paid-order-number">{order?.order_number}</div>
                        </div>
                        <div className="text-right">
                            <div className="data-label">Total Paid</div>
                            <div className="font-serif text-3xl mt-1">${order?.total?.toFixed(2)}</div>
                        </div>
                    </div>
                    <div className="hairline" />
                    <div className="space-y-2 text-sm">
                        {(order?.items || []).map((it) => (
                            <div key={it.product_id} className="flex justify-between">
                                <span className="text-secondary">{it.name} × {it.quantity}</span>
                                <span className="font-mono">${it.line_total?.toFixed(2)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="hairline" />
                    <p className="data-label text-[0.6rem] text-muted leading-relaxed">
                        Your order is being processed. You'll receive a shipping notification with tracking within 1–2 business days.
                        All Aminoplex products are FOR LABORATORY RESEARCH USE ONLY and NOT FOR HUMAN CONSUMPTION.
                    </p>
                </div>

                <div className="flex gap-3">
                    <Link to="/account/orders" className="btn flex-1" data-testid="view-orders">View My Orders</Link>
                    <Link to="/shop" className="btn btn-solid flex-1" data-testid="continue-shopping">Continue Browsing</Link>
                </div>
            </div>
        </div>
    );
}
